import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from '@angular/core';
import { BaseClient, ClientType } from '@models/clients';
import { LLCTransaction } from '@models/llc-mortgages';
import { MatterClientType } from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';

import {
	LlcRepoService,
	MortgageClientMappings,
} from '@repos/mortgages/llc.service';
import { MatterClients } from '@state/global/matter_clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import LogRocket from 'logrocket';

import { ICON } from '@core/util/icon';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import {
	ClientMapping,
	ClientMappings,
	MortgageInstructionGuarantor,
	MortgageInstructionMortgagor,
	MortgageInstructionPerson,
} from '@models/mortgages/mortgage-base';
import {
	getClientType,
	getLLCClientName,
	getLLCSigningOfficerName,
	getPrefilledNewClient,
	PrefilledNewClient,
} from '@core/util/llc';

export interface BorrowerMapping {
	llcClient: MortgageInstructionMortgagor | MortgageInstructionGuarantor;
	model: string;
	clientType: ClientType;
	guarantor: boolean;
	closerClientId: string;
	icon: ICON;
	prefilledNewClient: PrefilledNewClient;
	label: string;
	signingOfficer: boolean;
	occupation: string;
	mortgageId?: string;
}

@Component({
	selector: 'app-llc-borrowers-sync',
	templateUrl: './borrowers.component.html',
	styleUrls: ['./borrowers.component.scss'],
	standalone: false,
})
export class BorrowersComponent implements OnChanges {
	ICON = ICON;
	borrowers: BorrowerMapping[];
	borrowerIds: string[] = [];
	_transaction: LLCTransaction;
	clientTypes = ClientType;
	saving: false;

	@SelectSnapshot(SelectedMatter.matterBaseClientType)
	baseClientType: MatterClientType;
	@SelectSnapshot(MatterClients.get_clients) clients: BaseClient[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private snackbarService: SnackbarService,
		private llcRepoService: LlcRepoService,
	) {}

	@Output() next: EventEmitter<void> = new EventEmitter();

	public signingOfficersMapping: { [id: string]: string } = {};

	@Input() set transaction(transaction: LLCTransaction) {
		const newBorrowers = [];

		if (this._transaction) {
			return;
		}

		this._transaction = transaction;

		if (transaction) {
			transaction?.data?.mortgagors?.forEach((mortgagor) => {
				const type = getClientType(mortgagor.mortgagorType);
				newBorrowers.push(
					...this.buildClientMappings(
						mortgagor,
						type,
						transaction,
						'Borrower',
						false,
					),
				);
			});
			transaction?.data?.guarantors?.forEach((guarantor) => {
				const type = getClientType(guarantor.guarantorType);
				newBorrowers.push(
					...this.buildClientMappings(
						guarantor,
						type,
						transaction,
						'Guarantor',
						true,
						transaction.matterMortgageId,
					),
				);
			});
		}

		this.borrowers = cloneDeep(newBorrowers);

		this.updateSigningOfficerMapping();

		this.borrowerIds = this.borrowers.map((x) => x.closerClientId);
	}

	buildClientMappings = (
		mortgagor: MortgageInstructionMortgagor | MortgageInstructionGuarantor,
		type: ClientType,
		transaction: LLCTransaction,
		label: string,
		guarantor: boolean,
		mortgageId?: string,
	): BorrowerMapping[] => {
		const result: BorrowerMapping[] = [];
		const mappings = this.getCloserClientMapping(transaction, mortgagor.id);

		if (type === ClientType.PERSON) {
			result.push({
				llcClient: mortgagor,
				model: getLLCClientName(mortgagor),
				clientType: type,
				guarantor,
				closerClientId:
					mappings && mappings?.length > 0 ? mappings[0].closerClientId : null,
				icon: ICON.PERSON, // if there are mutliple matching ids then assume the 0 is a company and 1 is signing officer
				prefilledNewClient: getPrefilledNewClient(mortgagor, ClientType.PERSON),
				label,
				signingOfficer: false,
				occupation: mortgagor.occupation,
				mortgageId,
			});
		} else {
			const businessMapping = mappings.find((x) => x.type === 'company');
			const signingOfficerMapping = mappings.find(
				(x) => x.type === 'signingOfficer',
			);

			result.push({
				llcClient: mortgagor,
				model: mortgagor.companyName,
				clientType: type,
				guarantor,
				closerClientId: businessMapping ? businessMapping.closerClientId : null,
				icon: ICON.CORPORATION, // if there are mutliple matching ids then assume the 0 is a company and 1 is signing officer
				prefilledNewClient: getPrefilledNewClient(
					mortgagor,
					ClientType.CORPORATION,
				),
				label,
				signingOfficer: false,
				occupation: null,
				mortgageId,
			});
			if (mortgagor?.firstName?.length > 0) {
				result.push({
					llcClient: mortgagor,
					model: getLLCSigningOfficerName(mortgagor),
					clientType: type,
					guarantor,
					closerClientId: signingOfficerMapping
						? signingOfficerMapping.closerClientId
						: null,
					icon: ICON.PERSON, // if there are mutliple matching ids then assume the 0 is a company and 1 is signing officer
					prefilledNewClient: getPrefilledNewClient(
						mortgagor,
						ClientType.PERSON,
					),
					label: `${mortgagor.companyName} Signing Officer`,
					signingOfficer: true,
					occupation: mortgagor.occupation,
				});
			}
		}

		return result;
	};

	_skip() {
		this.next.emit();
	}

	async _next() {
		// create MortgageClientMappings
		const mappings: MortgageClientMappings[] = [];

		this.borrowers.forEach((borrower) => {
			const exists =
				this.clients.findIndex((x) => x.id === borrower.closerClientId) >= 0;

			mappings.push({
				closerClientId: borrower.closerClientId,
				mortgageClientId: borrower.llcClient.id,
				type: borrower.guarantor
					? MatterClientType.GUARANTOR
					: this.baseClientType,
				firstName: borrower.llcClient?.firstName,
				middleNames: borrower.llcClient?.middleName,
				lastName: borrower.llcClient?.lastName,
				name: borrower.llcClient.companyName,
				action: exists ? 'existing' : 'add',
				signingOfficer: borrower.signingOfficer,
				occupation: borrower.occupation,
				clientType: borrower.clientType,
				mortgageId: borrower.mortgageId,
			});
		});

		try {
			await this.llcRepoService.mapMortgageClientsToMatterClients(
				this.matterId,
				this._transaction.overview.transactionId,
				this._transaction.matterMortgageId,
				mappings,
			);
			this.next.emit();
		} catch (err) {
			console.log('error', err);
			this.snackbarService.openSnackBar({
				text: 'Unable to map LLC mortgage clients to matter clients. Please try again.',
				type: 'error',
			});
			LogRocket.error(
				'Unable to map llc mortgage clients to closer clients',
				err,
			);
		} finally {
			this.saving = false;
		}
	}

	getCloserClientMapping = (
		transaction: LLCTransaction,
		llcPersonId: number,
	): ClientMappings | null => {
		if (transaction?.clientMappings) {
			return transaction.clientMappings.filter(
				(mapping: ClientMapping) => mapping.llcId === llcPersonId,
			);
		}

		return null;
	};

	getModel = (
		client: MortgageInstructionPerson,
		type: ClientType,
		index: number,
	) => {
		// if the index is 0 then treat as company, if 1 then treat as signing officer
		if (type === ClientType.CORPORATION && index === 0) {
			return client?.companyName;
		}

		return getLLCClientName(client);
	};

	trackByllcClientId(index, value: BorrowerMapping) {
		return value.llcClient?.id ?? index;
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes?.borrowers?.currentValue) {
			this.borrowerIds = this.borrowers.map((x) => x.closerClientId);
		}
	}

	borrowerChanged() {
		this.borrowerIds = this.borrowers.map((x) => x.closerClientId);
		this.updateSigningOfficerMapping();
	}

	onRemove(borrower: BorrowerMapping) {
		// Make sure we remove the closerClientId from it's child (signing officer)
		this.borrowers.find(
			(x) => x.closerClientId === borrower.closerClientId,
		).closerClientId = null;

		this.borrowers.forEach((y) => {
			if (y.llcClient.id === borrower.llcClient.id) {
				y.closerClientId = null;
			}
		});

		this.borrowerChanged();
	}

	updateSigningOfficerMapping() {
		this.borrowers
			.filter((x) => x.signingOfficer)
			.forEach((x) => {
				this.signingOfficersMapping[x.llcClient.id] = this.borrowers.find(
					(y) => y.llcClient.id === x.llcClient.id && !y.signingOfficer,
				).closerClientId;
			});
	}
}
